import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Flex } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { NoData } from 'components/NoData'
import { tokens } from 'styling/tokens'
import { logAudit, patchDelivery } from 'features/materialReceipt/materialReceiptSlice'
import { discrepancyOf, fmt } from './materialReceiptData'
import { MaterialReceiptPage } from './MaterialReceiptShell'
import { FieldGrid, FieldLabel, FieldValue } from './MaterialReceipt.styled'

export const MaterialCases = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { deliveries, config } = useSelector((s: RootState) => s.materialReceipt)

  const cases = deliveries
    .map((d) => ({ d, disc: discrepancyOf(d, config.threshold) }))
    .filter((c) => c.disc?.breach)

  const resolve = (id: string, resolution: 'Confirmed genuine' | 'Disputed — supplier notified') => {
    dispatch(patchDelivery({ id, fields: { caseResolved: true, caseResolution: resolution } }))
    dispatch(logAudit({ ticket: id, action: 'Case resolved', actor: 'You (MANAGER)', reason: resolution, from: null, to: null }))
  }

  return (
    <MaterialReceiptPage requireManager title='Discrepancy cases' subtitle={`Arrival grade beyond ±${config.threshold}% of pre-shipment`}>
      {() => (
        <>
          {cases.length ? (
            <Flex column gap='1rem'>
              {cases.map(({ d, disc }) => (
                <Container key={d.id}>
                  <ContainerHeader>
                    <Flex between center style={{ width: '100%' }}>
                      <Flex center gap='0.6rem'>
                        <TitleText weight='bold'>{d.id}</TitleText>
                        <StatusPill status='danger'>{disc && disc.pct > 0 ? '+' : ''}{fmt(disc?.pct)}%</StatusPill>
                      </Flex>
                      {d.caseResolved ? (
                        <StatusPill status='success'>Resolved</StatusPill>
                      ) : (
                        <StatusPill status='warning'>Open</StatusPill>
                      )}
                    </Flex>
                  </ContainerHeader>
                  <ContainerContent>
                    <FieldGrid style={{ marginBottom: '1rem' }}>
                      <div><FieldLabel>Supplier</FieldLabel><FieldValue>{d.supplier}</FieldValue></div>
                      <div><FieldLabel>Source</FieldLabel><FieldValue>{d.source || '—'}</FieldValue></div>
                      <div><FieldLabel>Pre-shipment grade</FieldLabel><FieldValue>{fmt(d.preGrade)} g/t</FieldValue></div>
                      <div>
                        <FieldLabel>Arrival grade</FieldLabel>
                        <FieldValue style={{ color: tokens.status.danger.fg }}>{fmt(d.grade)} g/t</FieldValue>
                      </div>
                    </FieldGrid>

                    {d.caseResolved ? (
                      <BodyText grey style={{ margin: 0 }}>Resolution: <span style={{ color: tokens.text.primary, fontWeight: 600 }}>{d.caseResolution}</span></BodyText>
                    ) : (
                      <Flex gap='0.5rem' wrap>
                        <Button variant='secondary' size='sm' onClick={() => navigate(`/material-receipt/ticket/${d.id}`)}>Open ticket</Button>
                        <Button variant='success' size='sm' onClick={() => resolve(d.id, 'Confirmed genuine')}>Confirm genuine low-grade</Button>
                        <Button variant='danger' size='sm' onClick={() => resolve(d.id, 'Disputed — supplier notified')}>Flag as disputed</Button>
                      </Flex>
                    )}
                  </ContainerContent>
                </Container>
              ))}
            </Flex>
          ) : (
            <NoData text='No discrepancy cases — every arrival grade is within tolerance.' />
          )}
        </>
      )}
    </MaterialReceiptPage>
  )
}
